import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { UtilityService } from '../../service/utility.service';
import { ConfigService } from '../../service/config.service';
import { LandingFeature } from '../../models/PortalConfigurationView';
import {
  PortalFeatureDirective,
  PortalLoginRequiredDirective,
  PortalButtonDirective,
} from '../../directives';

interface Slide {
  image: string;
  alt: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PortalFeatureDirective,
    PortalLoginRequiredDirective,
    PortalButtonDirective,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
})
export class HomeComponent implements OnInit, OnDestroy {
  constructor(
    private utilityService: UtilityService,
    private configService: ConfigService,
  ) {}

  LoggedIn = JSON.parse(localStorage.getItem('login') || 'false');
  slides: Slide[] = [];
  currentSlide = 0;
  nextSlideIndex = 0;
  isAnimating = false;
  private configSub?: Subscription;
  private slideTimer?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    this.refreshSlides();
    this.configSub = this.configService.getConfig$().subscribe(() => {
      this.refreshSlides();
    });
  }

  ngOnDestroy(): void {
    if (this.configSub) this.configSub.unsubscribe();
    if (this.slideTimer) clearInterval(this.slideTimer);
  }

  private refreshSlides(): void {
    this.slides = this.resolveSlides();
    this.currentSlide = 0;
    this.nextSlideIndex = this.slides.length > 1 ? 1 : 0;
    if (this.slideTimer) {
      clearInterval(this.slideTimer);
      this.slideTimer = undefined;
    }
    this.autoSlide();
  }

  private resolveSlides(): Slide[] {
    const json = this.utilityService.portalConfig?.portalHeroImages;
    if (json) {
      try {
        const parsed = JSON.parse(json);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const slides = parsed
            .filter(
              (item: any) => item && typeof item.url === 'string' && item.url,
            )
            .map((item: any) => ({
              image: item.url,
              alt: item.fileName || 'Hero image',
            }));
          if (slides.length > 0) return slides;
        }
      } catch {
        // fall through to defaults
      }
    }
    return this.getDefaultSlides();
  }

  private getDefaultSlides(): Slide[] {
    return [
      { image: '../../assets/img/banner/body_background.jpg', alt: 'Slide 1' },
    ];
  }

  nextSlide(): void {
    if (this.isAnimating || this.slides.length < 2) return;
    this.isAnimating = true;
    this.nextSlideIndex = (this.currentSlide + 1) % this.slides.length;

    setTimeout(() => {
      this.currentSlide = this.nextSlideIndex;
      this.nextSlideIndex = (this.currentSlide + 1) % this.slides.length;
      this.isAnimating = false;
    }, 600);
  }

  prevSlide(): void {
    if (this.isAnimating || this.slides.length < 2) return;
    this.isAnimating = true;
    this.nextSlideIndex =
      (this.currentSlide - 1 + this.slides.length) % this.slides.length;

    setTimeout(() => {
      this.currentSlide = this.nextSlideIndex;
      this.nextSlideIndex = (this.currentSlide + 1) % this.slides.length;
      this.isAnimating = false;
    }, 600);
  }

  goToSlide(index: number): void {
    if (this.isAnimating || index === this.currentSlide) return;
    this.isAnimating = true;
    this.nextSlideIndex = index;

    setTimeout(() => {
      this.currentSlide = this.nextSlideIndex;
      this.nextSlideIndex = (this.currentSlide + 1) % this.slides.length;
      this.isAnimating = false;
    }, 600);
  }

  autoSlide(): void {
    if (this.slides.length > 1) {
      this.slideTimer = setInterval(() => this.nextSlide(), 4000);
    }
  }

  get welcomeParagraphs(): string[] {
    const content = this.utilityService.portalConfig?.portalLandingContent;
    if (content && content.trim()) {
      return content
        .split(/\r?\n/)
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
    }
    return this.getDefaultWelcomeParagraphs();
  }

  private getDefaultWelcomeParagraphs(): string[] {
    return [
      'Welcome to the Vendor Portal for the Stadium Authority.',
      'To register for new or future business with the Stadium Authority, complete the registration form by clicking on New Vendor Registration. For vendors currently engaging in business transactions, select Active Vendor Type. For vendors not currently engaging in business transactions or intending to engage in business transactions, select Prospective Vendor Type. To stay informed of all future bid opportunities relevant to your organization via email, select Yes under Include on Future Bid Opportunities.',
      'If you are an existing vendor with the Stadium Authority, submit any required invoices by clicking on Vendor Invoice Submission. Please attach your invoice and any other relevant documentation when submitting your invoice.',
      'Thank you for doing business with the Stadium Authority. For all inquiries, please contact procurement@pgh-sea.com.',
    ];
  }

  isFeatureVisible(index: 0 | 1 | 2): boolean {
    const features = this.landingFeatures;
    if (!features[index]?.enabled) return false;

    const cfg = this.utilityService.portalConfig;
    if (!cfg) return true;

    const flag =
      index === 0
        ? cfg.registrationFeatureEnabled
        : index === 1
          ? cfg.signupAndProfilingEnabled
          : cfg.invoiceFeatureEnabled;
    return String(flag).toLowerCase() !== 'false';
  }

  get landingFeatures(): LandingFeature[] {
    const json = this.utilityService.portalConfig?.landingFeatures;
    const defaults = this.getDefaultLandingFeatures();
    if (!json) return defaults;
    try {
      const parsed = JSON.parse(json);
      if (!Array.isArray(parsed) || parsed.length !== 3) return defaults;
      return parsed.map((f: any, i: number) => ({
        enabled: typeof f?.enabled === 'boolean' ? f.enabled : true,
        title: f?.title || defaults[i].title,
        content: f?.content || defaults[i].content,
        buttonText: f?.buttonText || defaults[i].buttonText,
      }));
    } catch {
      return defaults;
    }
  }

  private getDefaultLandingFeatures(): LandingFeature[] {
    return [
      {
        enabled: true,
        title: 'New Vendor Registration',
        content:
          'Note: The Stadium Authority will receive your registration. Once the Stadium Authority approves your registration, you will receive an automated email with your vendor ID and can proceed to step 2.',
        buttonText: 'Register Now',
      },
      {
        enabled: true,
        title: 'Profile Setup',
        content:
          'Once you receive your vendor ID, you can proceed with setting up a username and password to submit invoices and monitor invoice and Purchase Order history.',
        buttonText: 'Setup Profile',
      },
      {
        enabled: true,
        title: 'Vendor Invoice Submission',
        content:
          'Once profile is setup, you can begin submitting invoices through the Vendor Invoice Submission page and monitor progress via your profile.',
        buttonText: 'Submit Invoices',
      },
    ];
  }

  // Direct access to portal configuration
  get portalConfig() {
    return this.utilityService.portalConfig;
  }
}
